import { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/react";
import { getMyMeetsToday as getMyMeetsTodayApi } from "@/api/meets";
import ActiveMeetingCard from "../cards/active-meets-card";
import { Calendar, CalendarCheck, CalendarDays } from "lucide-react";
import { Skeleton } from "@heroui/react";
import PulledDownWrapper from "../wrappers/pulled-down-wrapper";
import MeetingCardSkeleton from "@/components/v2/meeting-card-skeleton";
import NoMeetsComp from "./no-meets-comp";
import MeetingCard from "../v2/meeting-card";
import { matchesMeetingSearch } from "@/utils/meeting-search";

const TodaysMeetsComp = ({ setNumMeetings, searchQuery }) => {
  const { session } = useSession();
  const [records, setRecord] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    getMyMeetsToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRecords = useMemo(
    () => records.filter((rec) => matchesMeetingSearch(rec, searchQuery)),
    [records, searchQuery],
  );

  useEffect(() => {
    setNumMeetings(filteredRecords.length);

  }, [filteredRecords]);

  async function getMyMeetsToday() {
    setIsLoading(true);
    setFetchError(false);
    const token = await session.getToken();
    getMyMeetsTodayApi(token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          const sorted = [..._resp.data.Records].sort((a, b) => {
            const tA = a?.start_time?.set ?? "";
            const tB = b?.start_time?.set ?? "";
            return tA.localeCompare(tB);
          });
          setRecord(sorted);
        } else {
          throw new Error("Failed to load today's meetings")
        }
      })
      .catch((_resp) => {
        console.error(_resp);
        setFetchError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  return (
    <PulledDownWrapper onRefresh={getMyMeetsToday}>
      <div className="max-w-[900px] gap-2 grid grid-cols-12">
        <div className="contents">
          {isLoading ? (
            <>
              {[0, 1, 2].map((i) => (
                <div key={i} className="col-span-12 flex flex-col mb-2">
                  <MeetingCardSkeleton />
                </div>
              ))}
            </>
          ) : (
            <>
              {filteredRecords.map((rec, _index) => (
                <div
                  key={rec._id}
                  className={`col-span-12 flex flex-col ${_index === filteredRecords.length - 1 ? "mb-40" : "mb-2"}`}
                >
                  <MeetingCard _record={rec} />
                </div>
              ))}
            </>
          )}
        </div>

        {!isLoading && fetchError && (
          <div className="col-span-12 flex flex-col items-center gap-3 py-10 text-center text-glass-foreground">
            <p className="font-semibold">Something went wrong!</p>
            <p className="text-sm text-glass-foreground-muted">We couldn't find your meetings.</p>
            <button
              className="text-sm underline"
              onClick={getMyMeetsToday}
            >
              Try again
            </button>
          </div>
        )}

        {!isLoading && !fetchError && filteredRecords.length === 0 && (
          <NoMeetsComp title={searchQuery ? "No matching Loopps" : undefined} />
        )}
      </div>
    </PulledDownWrapper>
  );
};

export default TodaysMeetsComp;